export type HomeHandoff = () => Promise<void> | void

interface StartupCoordinatorOptions {
  fallbackDelay?: number
  handoffTimeout?: number
}

interface StartupCoordinator {
  markRouterReady: (isHome: boolean) => void
  registerHomeHandoff: (handoff: HomeHandoff) => () => void
}

export function createStartupCoordinator(
  fadeOut: () => void,
  options: StartupCoordinatorOptions = {},
): StartupCoordinator {
  const fallbackDelay = options.fallbackDelay ?? 2500
  const handoffTimeout = options.handoffTimeout ?? 8000

  let completed = false
  let handoffRunning = false
  let initialRouteIsHome = false
  let routerReady = false
  let homeHandoff: HomeHandoff | undefined
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined
  let handoffTimer: ReturnType<typeof setTimeout> | undefined

  function clearTimers() {
    if (fallbackTimer) {
      clearTimeout(fallbackTimer)
      fallbackTimer = undefined
    }
    if (handoffTimer) {
      clearTimeout(handoffTimer)
      handoffTimer = undefined
    }
  }

  function complete() {
    if (completed) {
      return
    }
    completed = true
    clearTimers()
    fadeOut()
  }

  async function runHomeHandoff(handoff: HomeHandoff) {
    if (completed || handoffRunning) {
      return
    }

    handoffRunning = true
    clearTimers()
    handoffTimer = setTimeout(complete, handoffTimeout)

    try {
      await handoff()
    }
    catch {
      // The application must remain usable when the optional visual handoff fails.
    }
    finally {
      complete()
    }
  }

  function continueStartup() {
    if (!routerReady || completed) {
      return
    }

    if (!initialRouteIsHome) {
      complete()
      return
    }

    if (homeHandoff) {
      void runHomeHandoff(homeHandoff)
      return
    }

    if (!fallbackTimer) {
      fallbackTimer = setTimeout(complete, fallbackDelay)
    }
  }

  return {
    markRouterReady(isHome) {
      if (routerReady || completed) {
        return
      }
      routerReady = true
      initialRouteIsHome = isHome
      continueStartup()
    },
    registerHomeHandoff(handoff) {
      if (completed) {
        void Promise.resolve().then(handoff).catch(() => {})
        return () => {}
      }

      homeHandoff = handoff
      continueStartup()

      return () => {
        if (homeHandoff === handoff && !handoffRunning) {
          homeHandoff = undefined
        }
      }
    },
  }
}
